import React, { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { Dialog } from "@/components/ui/dialog";
import MyDialogContent from "@/components/MyDialogContent";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays } from "lucide-react";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import {
  formatYmdHmInTimeZone,
  getNowLocalDateTimeInput,
  isLocalDateTimePastInTimeZone,
  joinLocalDateTime,
  localDateTimeInputToUtcIso,
  splitLocalDateTime,
  utcIsoToLocalDateTimeInput,
} from "@/utils/patientTimezoneDateTime";

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function parseLocalDatetimeValue(v: string): Date | undefined {
  if (!v?.trim()) return undefined;
  const { dateYmd, timeHm } = splitLocalDateTime(v);
  if (!dateYmd) return undefined;
  const d = new Date(`${dateYmd}T${timeHm}:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function minutesToHHmm(total: number | string | null | undefined): string {
  if (total == null || total === "") return "";
  const n = typeof total === "string" ? parseInt(total, 10) : total;
  if (!Number.isFinite(n)) return "";
  const m = ((n % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${pad2(h)}:${pad2(min)}`;
}

function hhmmToMinutes(hhmm: string): number | undefined {
  if (!hhmm || typeof hhmm !== "string") return undefined;
  const m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return undefined;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(min)) return undefined;
  return (h * 60 + min) % 1440;
}

function quietStartFromPayload(p: any): string {
  const rawMin = p?.quietHoursStartMinute;
  if (rawMin != null && rawMin !== "" && Number.isFinite(Number(rawMin))) {
    return minutesToHHmm(Number(rawMin));
  }
  const t = p?.quietHoursStartTime;
  if (t == null || t === "") return "";
  const s = String(t).slice(0, 5);
  const m = hhmmToMinutes(s);
  return m !== undefined ? minutesToHHmm(m) : "";
}

function quietEndFromPayload(p: any): string {
  const rawMin = p?.quietHoursEndMinute;
  if (rawMin != null && rawMin !== "" && Number.isFinite(Number(rawMin))) {
    return minutesToHHmm(Number(rawMin));
  }
  const t = p?.quietHoursEndTime;
  if (t == null || t === "") return "";
  const s = String(t).slice(0, 5);
  const m = hhmmToMinutes(s);
  return m !== undefined ? minutesToHHmm(m) : "";
}

function SnoozeCustomDateTimeField({
  value,
  onChange,
  id,
  timeZone,
  disablePast,
}: {
  value: string;
  onChange: (localValue: string) => void;
  id?: string;
  timeZone?: string;
  disablePast?: boolean;
}) {
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const { dateYmd, timeHm } = splitLocalDateTime(value);
  const d = React.useMemo(() => parseLocalDatetimeValue(value), [value]);

  const patientNow = React.useMemo(() => {
    if (!disablePast || !timeZone?.trim()) return null;
    return formatYmdHmInTimeZone(new Date(), timeZone.trim());
  }, [disablePast, timeZone, popoverOpen]);

  const isPastInPatientTz = (local: string) =>
    isLocalDateTimePastInTimeZone(local, timeZone);

  const commit = (next: string) => {
    if (disablePast && isPastInPatientTz(next)) {
      toast.error(
        "Please select current or future date and time in the patient's timezone.",
      );
      return;
    }
    onChange(next);
  };

  const mergeDateKeepingTime = (day: Date) => {
    const ymd = format(day, "yyyy-MM-dd", { locale: enUS });
    let nextHm = timeHm || "00:00";
    if (
      disablePast &&
      patientNow &&
      ymd === patientNow.ymd &&
      nextHm < patientNow.hm
    ) {
      nextHm = patientNow.hm;
    }
    commit(joinLocalDateTime(ymd, nextHm));
  };

  const mergeTimeKeepingDate = (nextTimeHm: string) => {
    if (!dateYmd) return;
    commit(joinLocalDateTime(dateYmd, nextTimeHm));
  };

  return (
    <Popover modal={false} open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full justify-start font-normal"
          id={id}
        >
          <CalendarDays className="mr-2 size-4 opacity-70 shrink-0" />
          <span className="truncate">
            {d
              ? format(d, "MM/dd/yyyy h:mm a", { locale: enUS })
              : "Select date & time"}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[10001] w-auto overflow-hidden p-0"
        align="start"
      >
        <Calendar
          mode="single"
          locale={enUS}
          selected={d}
          disabled={
            disablePast && patientNow
              ? (day) => format(day, "yyyy-MM-dd") < patientNow.ymd
              : undefined
          }
          onSelect={(day) => {
            if (!day) return;
            mergeDateKeepingTime(day);
          }}
        />
        <div className="flex items-center gap-2 border-t p-3">
          <Label className="text-muted-foreground shrink-0">Time</Label>
          <input
            type="time"
            step={60}
            className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm h-9"
            value={timeHm}
            min={
              disablePast && patientNow && dateYmd === patientNow.ymd
                ? patientNow.hm
                : undefined
            }
            onChange={(e) => mergeTimeKeepingDate(e.target.value)}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

type NotifState = {
  timezone: string;
  channelEmailEnabled: boolean;
  channelSmsEnabled: boolean;
  quietHoursEnabled: boolean;
  quietHoursStartTime: string;
  quietHoursEndTime: string;
  // triageEnabled: boolean;
  snoozePreset: "" | "1h" | "4h" | "24h" | "custom";
  startDateTimeLocal: string;
  endDateTimeLocal: string;
  clearSnooze?: boolean;
};

type EventPrefsState = {
  NO_RPM_READING_3_DAYS: boolean;
  NO_CARE_PLAN_MONTHLY: boolean;
  RPM_ACHIEVEMENT_16PLUS: boolean;
  DEVICE_READING_RECEIVED: boolean;
};

const defaultEventPrefs = (): EventPrefsState => ({
  NO_RPM_READING_3_DAYS: true,
  NO_CARE_PLAN_MONTHLY: true,
  RPM_ACHIEVEMENT_16PLUS: true,
  DEVICE_READING_RECEIVED: true,
});

const defaultNotifState = (): NotifState => ({
  timezone: "",
  channelEmailEnabled: true,
  channelSmsEnabled: true,
  quietHoursEnabled: false,
  quietHoursStartTime: "",
  quietHoursEndTime: "",
  // triageEnabled: true,
  snoozePreset: "",
  startDateTimeLocal: "",
  endDateTimeLocal: "",
});

function openNativePicker(el: HTMLInputElement | null) {
  if (!el) return;
  try {
    if (typeof el.showPicker === "function") {
      el.showPicker();
    } else {
      el.focus();
      el.click();
    }
  } catch {
    el.focus();
    el.click();
  }
}

type Props = {
  patientId: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** IANA timezone for interpreting custom snooze start/end (patient context). */
  patientTimeZone?: string;
  /** Human-readable timezone label for UI hint. */
  patientTimezoneDisplay?: string;
};

export function PatientNotificationPreferencesDialog({
  patientId,
  open,
  onOpenChange,
  patientTimeZone,
  patientTimezoneDisplay,
}: Props) {
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifState, setNotifState] = useState<NotifState>(defaultNotifState);
  const [eventPrefs, setEventPrefs] = useState<EventPrefsState>(defaultEventPrefs);
  const quietStartRef = useRef<HTMLInputElement | null>(null);
  const quietEndRef = useRef<HTMLInputElement | null>(null);

  const loadPatientNotif = async () => {
    if (!patientId) return;
    try {
      setNotifLoading(true);
      const res: any = await AxiosInstance().get(
        `/notifications/patients/${patientId}/preferences`,
      );
      const root = res?.data?.data ?? res?.data;
      const p =
        root?.preferences ??
        root?.profile ??
        (typeof root === "object" && root !== null ? root : {});

      const ep = p?.eventPreferences ?? {};
      const derivedSnoozePreset =
        p?.snoozePreset ?? p?.lastSnoozePreset ?? "";

      setNotifState((prev) => ({
        ...prev,
        timezone: p?.timezone ?? "",
        channelEmailEnabled:
          typeof p?.emailEnabled === "boolean"
            ? p.emailEnabled
            : (p?.channelEmailEnabled ?? true),
        channelSmsEnabled:
          typeof p?.smsEnabled === "boolean"
            ? p.smsEnabled
            : (p?.channelSmsEnabled ?? true),
        quietHoursEnabled: !!p?.quietHoursEnabled,
        quietHoursStartTime: quietStartFromPayload(p),
        quietHoursEndTime: quietEndFromPayload(p),
        // triageEnabled:
        //   typeof ep?.TRIAGE_ALERT?.enabled === "boolean"
        //     ? ep.TRIAGE_ALERT.enabled
        //     : true,
        snoozePreset: derivedSnoozePreset,
        startDateTimeLocal:
          derivedSnoozePreset === "custom"
            ? utcIsoToLocalDateTimeInput(
                p?.snoozeStartAt,
                patientTimeZone || p?.timezone,
              )
            : "",
        endDateTimeLocal:
          derivedSnoozePreset === "custom"
            ? utcIsoToLocalDateTimeInput(
                p?.snoozeUntil,
                patientTimeZone || p?.timezone,
              )
            : "",
      }));
      setEventPrefs({
        NO_RPM_READING_3_DAYS: ep?.NO_RPM_READING_3_DAYS?.enabled ?? true,
        NO_CARE_PLAN_MONTHLY: ep?.NO_CARE_PLAN_MONTHLY?.enabled ?? true,
        RPM_ACHIEVEMENT_16PLUS: ep?.RPM_ACHIEVEMENT_16PLUS?.enabled ?? true,
        DEVICE_READING_RECEIVED: ep?.DEVICE_READING_RECEIVED?.enabled ?? true,
      });
    } catch {
      setNotifState(defaultNotifState());
      setEventPrefs(defaultEventPrefs());
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    if (open && patientId) {
      void loadPatientNotif();
    }
  }, [open, patientId, patientTimeZone]);

  const savePatientNotif = async () => {
    if (!patientId) return;
    try {
      setNotifSaving(true);
      let lastSnoozePreset = notifState.snoozePreset ;
      // console.log("lastSnoozePreset", lastSnoozePreset);
      const payload: Record<string, unknown> = {
        timezone: notifState.timezone || undefined,
        emailEnabled: notifState.channelEmailEnabled,
        smsEnabled: notifState.channelSmsEnabled,
        quietHoursEnabled: notifState.quietHoursEnabled,
        eventPreferences: {
          // TRIAGE_ALERT: { enabled: !!notifState.triageEnabled },
          NO_RPM_READING_3_DAYS: { enabled: !!eventPrefs.NO_RPM_READING_3_DAYS },
          NO_CARE_PLAN_MONTHLY: { enabled: !!eventPrefs.NO_CARE_PLAN_MONTHLY },
          RPM_ACHIEVEMENT_16PLUS: { enabled: !!eventPrefs.RPM_ACHIEVEMENT_16PLUS },
          DEVICE_READING_RECEIVED: {
            enabled: !!eventPrefs.DEVICE_READING_RECEIVED,
          },
        },
        snoozePreset: lastSnoozePreset || undefined,
        clearSnooze: lastSnoozePreset === ""  ? true : false,
      };

      if (notifState.quietHoursEnabled) {
        const sm = hhmmToMinutes(notifState.quietHoursStartTime);
        const em = hhmmToMinutes(notifState.quietHoursEndTime);
        if (sm !== undefined) payload.quietHoursStartMinute = sm;
        if (em !== undefined) payload.quietHoursEndMinute = em;
      }

      if (notifState.snoozePreset === "custom") {
        const tz = patientTimeZone || notifState.timezone;
        if (isLocalDateTimePastInTimeZone(notifState.startDateTimeLocal, tz)) {
          toast.error("Snooze start cannot be in the past.");
          return;
        }
        if (isLocalDateTimePastInTimeZone(notifState.endDateTimeLocal, tz)) {
          toast.error("Snooze end cannot be in the past.");
          return;
        }
        payload.startDateTime =
          localDateTimeInputToUtcIso(notifState.startDateTimeLocal, tz) ||
          undefined;
        payload.endDateTime =
          localDateTimeInputToUtcIso(notifState.endDateTimeLocal, tz) ||
          undefined;
      }

      await AxiosInstance().patch(
        `/notifications/patients/${patientId}/preferences`,
        payload,
      );
      toast.success("Notification settings saved");
      onOpenChange(false);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? "Failed to save notification settings",
      );
    } finally {
      setNotifSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MyDialogContent heading="Notification Settings">
        {notifLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2 p-3 mt-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Email</div>
                <Switch
                  checked={notifState.channelEmailEnabled}
                  onCheckedChange={(v) =>
                    setNotifState((p) => ({ ...p, channelEmailEnabled: v }))
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">SMS</div>
                <Switch
                  checked={notifState.channelSmsEnabled}
                  onCheckedChange={(v) =>
                    setNotifState((p) => ({ ...p, channelSmsEnabled: v }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2 rounded-md border p-3">
              <Label>Snooze</Label>
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center justify-between gap-2">
                  <Select
                    value={notifState.snoozePreset || ""}
                    onValueChange={(v) => {
                      const preset = v as NotifState["snoozePreset"];
                      const tz = patientTimeZone || notifState.timezone;
                      const now = getNowLocalDateTimeInput(tz);
                      setNotifState((p) => ({
                        ...p,
                        snoozePreset: preset,
                        startDateTimeLocal:
                          preset === "custom" && !p.startDateTimeLocal
                            ? now
                            : p.startDateTimeLocal,
                        endDateTimeLocal:
                          preset === "custom" && !p.endDateTimeLocal
                            ? now
                            : p.endDateTimeLocal,
                      }));
                    }}
                  >
                    <SelectTrigger
                      className="h-10 w-full"
                      aria-label="Snooze duration"
                    >
                      <SelectValue placeholder="Select snooze" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1h">1h</SelectItem>
                      <SelectItem value="4h">4h</SelectItem>
                      <SelectItem value="24h">24h</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  {(notifState.snoozePreset ||
    notifState.startDateTimeLocal ||
    notifState.endDateTimeLocal) ? (
    <Button
      onClick={() =>
        setNotifState((p) => ({
          ...p,
          snoozePreset: "",
          startDateTimeLocal: "",
          endDateTimeLocal: "",
          clearSnooze: true,
        }))
      }
      // className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
      aria-label="Clear"
    >
      Clear
    </Button>
  ) : null}
                </div>
              </div>
              {notifState.snoozePreset === "custom" ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Start</Label>
                      <SnoozeCustomDateTimeField
                        id="patient-snooze-start"
                        value={notifState.startDateTimeLocal}
                        timeZone={patientTimeZone || notifState.timezone}
                        disablePast
                        onChange={(v) =>
                          setNotifState((p) => ({
                            ...p,
                            startDateTimeLocal: v,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>End</Label>
                      <SnoozeCustomDateTimeField
                        id="patient-snooze-end"
                        value={notifState.endDateTimeLocal}
                        timeZone={patientTimeZone || notifState.timezone}
                        disablePast
                        onChange={(v) =>
                          setNotifState((p) => ({
                            ...p,
                            endDateTimeLocal: v,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 font-bold text-xs text-muted-foreground">
                    <span>
                      Please select the time as per patient&apos;s timezone{" "}
                      <span className="font-medium text-green-600">
                        {patientTimezoneDisplay ||
                          patientTimeZone ||
                          notifState.timezone ||
                          "—"}
                      </span>
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <Label>Quiet Hours</Label>
                <Switch
                  checked={notifState.quietHoursEnabled}
                  onCheckedChange={(v) =>
                    setNotifState((p) => ({ ...p, quietHoursEnabled: v }))
                  }
                />
              </div>
              {notifState.quietHoursEnabled ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Start</Label>
                    <div
                      className="cursor-pointer"
                      onClick={() => openNativePicker(quietStartRef.current)}
                    >
                      <input
                        ref={quietStartRef}
                        type="time"
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm date-input-native"
                        value={notifState.quietHoursStartTime}
                        onClick={() =>
                          openNativePicker(quietStartRef.current)
                        }
                        onFocus={() =>
                          openNativePicker(quietStartRef.current)
                        }
                        onChange={(e) =>
                          setNotifState((p) => ({
                            ...p,
                            quietHoursStartTime: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label>End</Label>
                    <div
                      className="cursor-pointer"
                      onClick={() => openNativePicker(quietEndRef.current)}
                    >
                      <input
                        ref={quietEndRef}
                        type="time"
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm date-input-native"
                        value={notifState.quietHoursEndTime}
                        onClick={() => openNativePicker(quietEndRef.current)}
                        onFocus={() => openNativePicker(quietEndRef.current)}
                        onChange={(e) =>
                          setNotifState((p) => ({
                            ...p,
                            quietHoursEndTime: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Triage Alert</div>
                <Switch
                  checked={notifState.triageEnabled}
                  onCheckedChange={(v) =>
                    setNotifState((p) => ({ ...p, triageEnabled: v }))
                  }
                />
              </div>
            </div> */}

            <div className="space-y-1">
              <Label>Event Preferences</Label>
              <div className="rounded-md border p-3 space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={eventPrefs.NO_RPM_READING_3_DAYS}
                    onCheckedChange={(v) =>
                      setEventPrefs((p) => ({
                        ...p,
                        NO_RPM_READING_3_DAYS: v === true,
                      }))
                    }
                  />
                  <div>
                    <div className="text-sm font-medium">No RPM Reading - 3 Days</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={eventPrefs.NO_CARE_PLAN_MONTHLY}
                    onCheckedChange={(v) =>
                      setEventPrefs((p) => ({
                        ...p,
                        NO_CARE_PLAN_MONTHLY: v === true,
                      }))
                    }
                  />
                  <div>
                    <div className="text-sm font-medium">No Care Plan Monthly</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={eventPrefs.RPM_ACHIEVEMENT_16PLUS}
                    onCheckedChange={(v) =>
                      setEventPrefs((p) => ({
                        ...p,
                        RPM_ACHIEVEMENT_16PLUS: v === true,
                      }))
                    }
                  />
                  <div>
                    <div className="text-sm font-medium">RPM Achievement 16+</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={eventPrefs.DEVICE_READING_RECEIVED}
                    onCheckedChange={(v) =>
                      setEventPrefs((p) => ({
                        ...p,
                        DEVICE_READING_RECEIVED: v === true,
                      }))
                    }
                  />
                  <div>
                    <div className="text-sm font-medium">Device Reading Received</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={savePatientNotif} disabled={notifSaving}>
                {notifSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        )}
      </MyDialogContent>
    </Dialog>
  );
}
